import type { MetaFunction } from '@remix-run/node';
import { Link, useNavigate } from '@remix-run/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe,
  BookOpen,
  Rocket,
  Sparkles,
  Image,
  Target,
  ChevronDown,
  ArrowRight,
  Check,
  Zap,
  Shield,
  BarChart3,
  Layers,
  Clock,
  Menu,
  X,
  FileText,
  Share2,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useAppStore } from '~/stores';

export const meta: MetaFunction = () => [
  { title: 'Deleg8 Studio — Paste Your URL. Get a Full Campaign.' },
  {
    name: 'description',
    content:
      'Paste your website URL. Get social posts, email campaigns, and on-brand visuals — generated in minutes from your brand DNA.',
  },
  { property: 'og:title', content: 'Deleg8 Studio — One URL In, Full Campaign Out' },
  {
    property: 'og:description',
    content:
      'Paste your URL. Deleg8 Studio learns your brand and generates ready-to-publish content for Instagram, LinkedIn, email, and more.',
  },
];

/* ------------------------------------------------------------------ */
/*  Animation wrapper                                                  */
/* ------------------------------------------------------------------ */

function FadeIn({
  children,
  delay = 0,
  className = '',
  direction = 'up',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  direction?: 'up' | 'down' | 'left' | 'right';
}) {
  const offsets = { up: { y: 30 }, down: { y: -30 }, left: { x: 30 }, right: { x: -30 } };

  return (
    <motion.div
      initial={{ opacity: 0, ...offsets[direction] }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const STEPS = [
  {
    number: '01',
    title: 'Connect Your Brand',
    description:
      'Paste your website URL or upload brand docs. Our AI extracts your brand DNA in under 3 minutes.',
    icon: Globe,
    accent: 'text-creative-400',
    bgAccent: 'bg-creative-500/10',
  },
  {
    number: '02',
    title: 'Build Your Narrative',
    description:
      "AI composes your brand story — problem, promise, proof, and voice. Review only what's uncertain.",
    icon: BookOpen,
    accent: 'text-accent-400',
    bgAccent: 'bg-accent-500/10',
  },
  {
    number: '03',
    title: 'Generate Everything',
    description:
      'Instagram captions, LinkedIn posts, email copy, and matching visuals — generated for every channel, ready to publish.',
    icon: Rocket,
    accent: 'text-heat-400',
    bgAccent: 'bg-heat-500/10',
  },
];

const FEATURES = [
  {
    icon: Sparkles,
    title: 'Brand Extraction',
    description: 'Drop your URL and walk away. We crawl your site, read your docs, and structure your entire brand context — automatically.',
    wide: true,
  },
  {
    icon: FileText,
    title: 'Brand Story Engine',
    description: 'Your brand narrative — problem, promise, proof, and voice — structured, versioned, and locked as your single source of truth.',
  },
  {
    icon: Target,
    title: 'Campaign Templates',
    description: 'Product launch, seasonal promo, social sprint — pick a template and generate a full campaign in one click.',
  },
  {
    icon: Image,
    title: 'On-Brand Images',
    description: 'Social graphics and email banners generated alongside your copy, matching your brand style. No design tools needed.',
  },
  {
    icon: Share2,
    title: 'Every Platform, One Click',
    description: 'Instagram captions, LinkedIn posts, email subject lines, Facebook ads — sized and formatted for each channel.',
  },
  {
    icon: Shield,
    title: 'Source-Backed Copy',
    description: 'Every line of copy is traceable to your actual brand docs. No hallucinations, no guesswork, no off-brand surprises.',
  },
];

const FAQ_DATA = [
  {
    question: 'Will it actually sound like my brand, or generic AI?',
    answer:
      "It sounds like YOU. Deleg8 doesn't use generic prompts — it builds a versioned brand narrative from your actual website and docs. Every piece of generated content is grounded in your real voice, messaging, and proof points. If your website says it, Deleg8 knows it.",
  },
  {
    question: 'What happens if my website doesn\u2019t have much copy?',
    answer:
      "That's fine. You can upload brand documents, pitch decks, or style guides alongside your URL. The more context you give it, the sharper the output — but even a basic website gives Deleg8 enough to extract your core positioning and voice.",
  },
  {
    question: 'How is this different from just using ChatGPT?',
    answer:
      "ChatGPT doesn't know your brand. Every session starts from zero — you paste guidelines, re-explain your tone, and still get inconsistent results. Deleg8 builds a permanent brand narrative that every campaign draws from. Same voice, every channel, every time.",
  },
  {
    question: 'What kind of content does it create?',
    answer:
      'Social media posts (Instagram, LinkedIn, Twitter, Facebook), email campaigns, and matching visual assets — all generated together from one brief. It creates the content itself, not marketing strategy or ad placement.',
  },
  {
    question: 'Is this for organic content or paid ads?',
    answer:
      "Primarily organic social content and email marketing. The generated copy and visuals also work great as starting points for paid social ads. Deleg8 doesn't manage ad budgets or placements — it creates the creative.",
  },
  {
    question: 'Is my brand data secure?',
    answer:
      'Yes. Your data is encrypted at rest and in transit. We never train AI models on your brand data, and you retain full ownership of everything generated.',
  },
  {
    question: 'How much does it cost?',
    answer:
      'Plans start at $19/month. Growth is $69/month and Pro is $149/month. There is no free trial live right now, and you can choose the plan that fits your team from the pricing page.',
  },
];

/* ------------------------------------------------------------------ */
/*  FAQ accordion item                                                 */
/* ------------------------------------------------------------------ */

function FAQItem({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-white/[0.08]">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between py-5 text-left transition-colors hover:text-creative-300"
      >
        <span className={`pr-4 text-base font-medium ${isOpen ? 'text-creative-300' : 'text-white/90'}`}>{question}</span>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className={`h-5 w-5 flex-shrink-0 ${isOpen ? 'text-creative-400' : 'text-white/60'}`} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm leading-relaxed text-white/60">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/*  Landing page content                                               */
/* ------------------------------------------------------------------ */

function LandingPageContent() {
  const navigate = useNavigate();
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate('/app');
  }, [isAuthenticated, navigate]);

  const scrollTo = useCallback(
    (id: string) => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    },
    [],
  );

  if (isAuthenticated) return null;

  return (
    <div className="min-h-screen overflow-x-hidden bg-surface-950 text-white">
      {/* ==================== NAVIGATION ==================== */}
      <nav className="fixed left-0 right-0 top-0 z-50 border-b border-white/[0.06] bg-surface-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Deleg8 Studio" className="h-8 w-8" loading="lazy" />
            <span className="text-lg font-bold">Deleg8 Studio</span>
          </div>

          {/* Desktop links */}
          <div className="hidden items-center gap-8 md:flex">
            <button
              onClick={() => scrollTo('how-it-works')}
              className="text-sm text-white/60 transition hover:text-white"
            >
              How It Works
            </button>
            <button
              onClick={() => scrollTo('features')}
              className="text-sm text-white/60 transition hover:text-white"
            >
              Features
            </button>
            <button
              onClick={() => scrollTo('faq')}
              className="text-sm text-white/60 transition hover:text-white"
            >
              FAQ
            </button>
            <Link
              to="/pricing"
              className="text-sm text-white/60 transition hover:text-white"
            >
              Pricing
            </Link>
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <Link
              to="/login"
              className="text-sm font-medium text-white/70 transition hover:text-white"
            >
              Log in
            </Link>
            <Link
              to="/pricing"
              className="rounded-xl bg-gradient-to-r from-creative-600 via-accent-500 to-heat-500 px-4 py-2 text-sm font-semibold text-white shadow-glow transition hover:brightness-110"
            >
              See Pricing
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white/70 md:hidden"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-t border-white/5 bg-surface-950/95 backdrop-blur-xl md:hidden"
            >
              <div className="flex flex-col gap-4 px-6 py-4">
                <button
                  onClick={() => scrollTo('how-it-works')}
                  className="text-left text-sm text-white/60"
                >
                  How It Works
                </button>
                <button
                  onClick={() => scrollTo('features')}
                  className="text-left text-sm text-white/60"
                >
                  Features
                </button>
                <button
                  onClick={() => scrollTo('faq')}
                  className="text-left text-sm text-white/60"
                >
                  FAQ
                </button>
                <Link to="/pricing" className="text-sm text-white/60">
                  Pricing
                </Link>
                <Link to="/login" className="text-sm text-white/60">
                  Log in
                </Link>
                <Link
                  to="/pricing"
                  className="rounded-xl bg-gradient-to-r from-creative-600 via-accent-500 to-heat-500 px-4 py-2.5 text-center text-sm font-semibold text-white"
                >
                  See Pricing
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ==================== HERO ==================== */}
      <section className="relative flex min-h-[70vh] items-center justify-center px-6 pt-20 sm:min-h-[80vh]">
        {/* Ambient blobs — stronger intensity */}
        <div className="pointer-events-none absolute -left-40 top-20 h-[600px] w-[600px] rounded-full bg-creative-700/30 blur-[120px]" />
        <div className="pointer-events-none absolute -right-32 bottom-20 h-[500px] w-[500px] rounded-full bg-accent-600/25 blur-[100px]" />
        <div className="pointer-events-none absolute left-1/2 top-1/3 h-[300px] w-[300px] -translate-x-1/2 rounded-full bg-heat-600/15 blur-[80px]" />

        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <FadeIn>
            <motion.span
              animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
              style={{ backgroundSize: '200% 200%' }}
              className="inline-flex items-center gap-2 rounded-full border border-creative-500/20 bg-gradient-to-r from-creative-500/10 via-accent-500/10 to-creative-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-creative-300 backdrop-blur-sm"
            >
              <Sparkles className="h-3.5 w-3.5" />
              URL → Brand DNA → Campaign
            </motion.span>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h1 className="mt-8 text-4xl font-bold leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
              Paste your URL.{' '}
              <br className="hidden sm:block" />
              Get a{' '}
              <span className="bg-gradient-to-r from-creative-400 via-accent-400 to-heat-400 bg-clip-text text-transparent">
                full campaign.
              </span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/60 sm:text-xl">
              Deleg8 Studio captures your brand DNA from a single URL and generates on-brand copy
              and visuals for Instagram, LinkedIn, email, and more — in minutes, not weeks.
            </p>
          </FadeIn>

          <FadeIn delay={0.3}>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/pricing"
                className="group relative flex items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-creative-600 via-accent-500 to-heat-500 px-7 py-3.5 text-sm font-semibold text-white shadow-glow transition hover:shadow-[0_0_30px_-5px_rgba(168,85,247,0.5)]"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Choose Your Plan
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3, ease: 'easeInOut' }}
                />
              </Link>
              <button
                onClick={() => scrollTo('how-it-works')}
                className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white/80 backdrop-blur-sm transition hover:border-white/25 hover:bg-white/10 hover:text-white"
              >
                See How It Works
              </button>
            </div>
          </FadeIn>

          {/* Animated shimmer divider */}
          <FadeIn delay={0.4}>
            <div className="relative mx-auto mt-16 h-px w-64">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-creative-500/40 to-transparent" />
              <motion.div
                className="absolute inset-y-0 w-16 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                animate={{ x: ['-4rem', '16rem'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ==================== PROBLEM ==================== */}
      <section className="relative px-4 py-16 sm:px-6 sm:py-24">
        {/* Section divider */}
        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-3/4 -translate-x-1/2 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

        <div className="mx-auto max-w-6xl">
          <FadeIn>
            <p className="text-center text-xs font-semibold uppercase tracking-widest text-creative-400">
              The Problem
            </p>
            <h2 className="mt-3 text-center text-3xl font-bold text-white sm:text-4xl">
              Creative production is broken.
            </h2>
          </FadeIn>

          <div className="mt-16 grid gap-6 sm:grid-cols-3">
            {[
              {
                icon: Layers,
                title: 'Staring at a Blank Page. Again.',
                body: 'You need to post today but have no idea what to say. 49% of marketers report this exact problem \u2014 and it never gets easier.',
                accent: 'from-creative-500/20 to-creative-400/10',
                iconColor: 'text-creative-400',
                borderHover: 'hover:border-creative-500/30',
              },
              {
                icon: Clock,
                title: 'You\u2019re Posting Less Than You Should',
                body: 'You know the playbook: be consistent, be everywhere. But content takes so long to make that you post once when you planned for five.',
                accent: 'from-accent-500/20 to-accent-400/10',
                iconColor: 'text-accent-400',
                borderHover: 'hover:border-accent-500/30',
              },
              {
                icon: BarChart3,
                title: 'Your Brand Sounds Different Everywhere',
                body: 'LinkedIn sounds corporate. Instagram sounds casual. Email sounds like someone else. No single source of truth holding it together.',
                accent: 'from-heat-500/20 to-heat-400/10',
                iconColor: 'text-heat-400',
                borderHover: 'hover:border-heat-500/30',
              },
            ].map((problem, i) => (
              <FadeIn key={problem.title} delay={i * 0.1}>
                <div className={`group rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.06] sm:p-8 ${problem.borderHover}`}>
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${problem.accent}`}>
                    <problem.icon className={`h-6 w-6 ${problem.iconColor}`} />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-white">{problem.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{problem.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section id="how-it-works" className="relative px-4 py-16 sm:px-6 sm:py-24">
        <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[600px] -translate-x-1/2 rounded-full bg-creative-700/15 blur-[120px]" />
        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-3/4 -translate-x-1/2 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

        <div className="relative z-10 mx-auto max-w-6xl">
          <FadeIn>
            <p className="text-center text-xs font-semibold uppercase tracking-widest text-accent-400">
              How It Works
            </p>
            <h2 className="mt-3 text-center text-3xl font-bold text-white sm:text-4xl">
              Three steps to your first campaign.
            </h2>
          </FadeIn>

          <div className="mt-16 grid gap-8 lg:grid-cols-3">
            {STEPS.map((step, i) => (
              <FadeIn key={step.number} delay={i * 0.15}>
                <div className="group relative h-full overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 backdrop-blur-sm transition-all duration-300 hover:border-white/[0.15] hover:bg-white/[0.06] sm:p-8">
                  {/* Gradient top edge on hover */}
                  <div className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent ${i === 0 ? 'via-creative-500/50' : i === 1 ? 'via-accent-500/50' : 'via-heat-500/50'} to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />

                  {/* Step number */}
                  <span className={`text-5xl font-bold ${step.accent}`} style={{ opacity: 0.15 }}>
                    {step.number}
                  </span>

                  {/* Icon */}
                  <div
                    className={`mt-4 flex h-14 w-14 items-center justify-center rounded-2xl ${step.bgAccent} transition-transform duration-300 group-hover:scale-110`}
                  >
                    <step.icon className={`h-7 w-7 ${step.accent}`} />
                  </div>

                  <h3 className="mt-5 text-xl font-semibold text-white">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{step.description}</p>

                  {/* Mini-mockup */}
                  <div className="mt-6 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
                    {i === 0 && (
                      <>
                        <div className="flex items-center gap-2 rounded-lg bg-white/[0.06] px-3 py-2">
                          <Globe className="h-4 w-4 text-white/60" />
                          <span className="text-xs text-white/50">https://yourbrand.com</span>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <div className="h-1.5 flex-1 rounded-full bg-white/[0.06]">
                            <motion.div
                              className="h-full rounded-full bg-gradient-to-r from-creative-500 to-accent-500"
                              initial={{ width: '0%' }}
                              whileInView={{ width: '78%' }}
                              viewport={{ once: true }}
                              transition={{ duration: 1.5, delay: 0.5, ease: 'easeOut' }}
                            />
                          </div>
                          <span className="text-[10px] text-white/60">Extracting...</span>
                        </div>
                      </>
                    )}
                    {i === 1 && (
                      <div className="space-y-1.5">
                        {['Problem', 'Promise', 'Voice & Tone'].map((section) => (
                          <div
                            key={section}
                            className="flex items-center justify-between rounded-lg bg-white/[0.06] px-3 py-1.5"
                          >
                            <span className="text-[10px] font-semibold text-white/60">
                              {section}
                            </span>
                            <Check className="h-3 w-3 text-emerald-400" />
                          </div>
                        ))}
                        <div className="mt-1 flex items-center gap-1.5">
                          <div className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                          <span className="text-[10px] font-medium text-emerald-400/80">
                            92% confidence
                          </span>
                        </div>
                      </div>
                    )}
                    {i === 2 && (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          {['LinkedIn', 'Instagram', 'Email'].map((ch) => (
                            <span
                              key={ch}
                              className="rounded-full bg-white/[0.06] px-2.5 py-0.5 text-[10px] font-medium text-white/60"
                            >
                              {ch}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-1.5 rounded-lg bg-white/[0.06] px-3 py-1.5">
                          <Sparkles className="h-3 w-3 text-heat-400" />
                          <span className="text-[10px] font-medium text-white/50">
                            5 variants generated
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Connecting arrow */}
                  {i < STEPS.length - 1 && (
                    <div className="absolute -right-4 top-1/2 hidden -translate-y-1/2 lg:block">
                      <ArrowRight className="h-5 w-5 text-white/20" />
                    </div>
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FEATURES BENTO ==================== */}
      <section id="features" className="relative px-4 py-16 sm:px-6 sm:py-24">
        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-3/4 -translate-x-1/2 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

        <div className="mx-auto max-w-6xl">
          <FadeIn>
            <p className="text-center text-xs font-semibold uppercase tracking-widest text-heat-400">
              Features
            </p>
            <h2 className="mt-3 text-center text-3xl font-bold text-white sm:text-4xl">
              Everything from URL to publish.
            </h2>
          </FadeIn>

          <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature, i) => (
              <FadeIn
                key={feature.title}
                delay={i * 0.08}
                className={feature.wide ? 'sm:col-span-2' : ''}
              >
                <div className="group relative h-full overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-creative-500/30 hover:bg-white/[0.06]">
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-creative-500/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-creative-500/20 to-accent-500/20 transition-transform duration-300 group-hover:scale-110">
                    <feature.icon className="h-5 w-5 text-creative-400" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-white">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">
                    {feature.description}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SAMPLE OUTPUT ==================== */}
      <section className="relative px-4 py-16 sm:px-6 sm:py-24">
        <div className="pointer-events-none absolute -right-40 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-accent-700/15 blur-[100px]" />
        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-3/4 -translate-x-1/2 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

        <div className="relative z-10 mx-auto max-w-6xl">
          <FadeIn>
            <p className="text-center text-xs font-semibold uppercase tracking-widest text-creative-400">
              Sample Output
            </p>
            <h2 className="mt-3 text-center text-3xl font-bold text-white sm:text-4xl">
              One URL. One brief.{' '}
              <span className="bg-gradient-to-r from-creative-400 via-accent-400 to-heat-400 bg-clip-text text-transparent">
                All of this.
              </span>
            </h2>
          </FadeIn>

          {/* Prominent proof callout */}
          <FadeIn delay={0.1}>
            <div className="mx-auto mt-8 flex max-w-2xl items-center justify-center gap-3 rounded-2xl border border-creative-500/20 bg-creative-500/[0.07] px-6 py-4 backdrop-blur-sm">
              <Sparkles className="h-5 w-5 flex-shrink-0 text-creative-400" />
              <p className="text-center text-sm font-medium text-white/80">
                Every image below was generated by Deleg8 from a single URL — including the copy.
              </p>
            </div>
          </FadeIn>

          {/* Masonry gallery organized by channel */}
          <div className="mt-12 columns-1 gap-4 sm:columns-2 lg:columns-3" style={{ columnFill: 'balance' }}>
            {[
              { src: '/images/samples/linkedin-1.jpg', channel: 'LinkedIn', color: '#0A66C2', copy: 'Weeks to minutes. Same brand. Better campaigns.' },
              { src: '/images/samples/instagram-post-1.jpg', channel: 'Instagram', color: '#E4405F', copy: 'From scattered to scalable in 3 minutes.' },
              { src: '/images/samples/instagram-story-1.jpg', channel: 'Instagram Story', color: '#E4405F', copy: 'Weeks \u2192 Minutes' },
              { src: '/images/samples/facebook-1.jpg', channel: 'Facebook', color: '#1877F2', copy: 'What if every campaign took minutes, not weeks?' },
              { src: '/images/samples/instagram-post-2.jpg', channel: 'Instagram', color: '#E4405F', copy: 'Campaigns in minutes, not weeks.' },
              { src: '/images/samples/instagram-story-2.jpg', channel: 'Instagram Story', color: '#E4405F', copy: '5+ variants per campaign.' },
              { src: '/images/samples/linkedin-2.jpg', channel: 'LinkedIn', color: '#0A66C2', copy: '3 minutes to capture your entire brand DNA.' },
              { src: '/images/samples/instagram-story-3.jpg', channel: 'Instagram Story', color: '#E4405F', copy: '3 min brand capture.' },
            ].map((sample, i) => (
              <FadeIn key={sample.src} delay={i * 0.06} className="mb-4 break-inside-avoid">
                <div className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03] transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:shadow-lg hover:shadow-black/20">
                  <img
                    src={sample.src}
                    alt={`${sample.channel} — ${sample.copy}`}
                    className="w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                    decoding="async"
                    loading="lazy"
                  />
                  <div className="absolute left-3 top-3">
                    <div
                      className="flex h-6 items-center gap-1.5 rounded-full px-2.5 text-[10px] font-semibold backdrop-blur-md"
                      style={{ backgroundColor: `${sample.color}33`, color: sample.color }}
                    >
                      {sample.channel}
                    </div>
                  </div>
                  {/* Copy caption */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-4 pb-4 pt-10 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <p className="text-xs font-medium leading-relaxed text-white/90">{sample.copy}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== GET STARTED ==================== */}
      <section id="get-started" className="relative px-4 py-16 sm:px-6 sm:py-24">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-creative-700/20 blur-[120px]" />
        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-3/4 -translate-x-1/2 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

        <div className="relative z-10 mx-auto max-w-xl text-center">
          <FadeIn>
            <span className="inline-flex items-center gap-2 rounded-full border border-heat-500/20 bg-heat-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-heat-400">
              <Zap className="h-3.5 w-3.5" />
              Plans from $19/mo
            </span>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h2 className="mt-6 text-3xl font-bold text-white sm:text-4xl">Ready to see what your brand generates?</h2>
            <p className="mt-4 text-sm text-white/60">
              Paste your URL and get your first campaign in minutes.
            </p>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Link
                to="/pricing"
                className="group relative flex items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-creative-600 via-accent-500 to-heat-500 px-8 py-4 text-base font-semibold text-white shadow-glow transition hover:shadow-[0_0_30px_-5px_rgba(168,85,247,0.5)]"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Choose Your Plan
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3, ease: 'easeInOut' }}
                />
              </Link>
              <Link
                to="/login"
                className="text-sm font-medium text-white/60 transition hover:text-white"
              >
                Already have an account? Sign in
              </Link>
            </div>
            <p className="mt-4 text-xs text-white/60">
              Paid plans start at $19/month. No trial is active right now.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section id="faq" className="relative px-4 py-16 sm:px-6 sm:py-24">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: FAQ_DATA.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
              })),
            }),
          }}
        />
        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-3/4 -translate-x-1/2 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

        <div className="mx-auto max-w-2xl">
          <FadeIn>
            <p className="text-center text-xs font-semibold uppercase tracking-widest text-creative-400">
              FAQ
            </p>
            <h2 className="mt-3 text-center text-3xl font-bold text-white sm:text-4xl">
              Questions? Answers.
            </h2>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="mt-12">
              {FAQ_DATA.map((item, i) => (
                <FAQItem
                  key={item.question}
                  question={item.question}
                  answer={item.answer}
                  isOpen={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="border-t border-white/[0.06] px-4 py-8 sm:px-6 sm:py-12">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Deleg8 Studio" className="h-6 w-6" loading="lazy" />
            <span className="text-sm font-semibold text-white/80">Deleg8 Studio</span>
          </div>
          <p className="text-xs text-white/60">
            &copy; {new Date().getFullYear()} Deleg8 Studio. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/privacy"
              className="text-xs text-white/60 transition hover:text-white/80"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="text-xs text-white/60 transition hover:text-white/80"
            >
              Terms
            </Link>
            <Link
              to="/login"
              className="text-xs text-white/60 transition hover:text-white/80"
            >
              Log in
            </Link>
            <Link
              to="/pricing"
              className="text-xs text-white/60 transition hover:text-white/80"
            >
              Create account
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Exported page                                                      */
/* ------------------------------------------------------------------ */

export default function LandingPage() {
  return <LandingPageContent />;
}
